/**
 * Ritual Invites Hooks
 * Hooks for accepting/rejecting/resending/withdrawing ritual organizer invites
 * and ritual member invites
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  acceptOrganizerInvite as acceptOrganizerInviteApi,
  acceptRitualMemberInvite,
  getRitualMemberInvitees,
  inviteRitualMember,
  rejectOrganizerInvite as rejectOrganizerInviteApi,
  rejectRitualMemberInvite,
  resendOrganizerInvite as resendOrganizerInviteApi,
  resendRitualMemberInvite,
  withdrawOrganizerInvite as withdrawOrganizerInviteApi,
  withdrawRitualMemberInvite,
} from "../services/ritual.service"

export function useAcceptOrganizerInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => acceptOrganizerInviteApi(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation accepted!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      queryClient.invalidateQueries({ queryKey: ["community-ritual-requests"] })
      queryClient.invalidateQueries({ queryKey: ["community-statistics"] })
      queryClient.invalidateQueries({ queryKey: ["community-rituals"] })
    },
    onError: () => {
      toast.error("Failed to accept invitation")
    },
  })

  return {
    acceptOrganizerInvite: mutation.mutate,
    acceptOrganizerInviteAsync: mutation.mutateAsync,
    isAccepting: mutation.isPending,
  }
}

export function useRejectOrganizerInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => rejectOrganizerInviteApi(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation declined")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      queryClient.invalidateQueries({ queryKey: ["community-ritual-requests"] })
      queryClient.invalidateQueries({ queryKey: ["community-statistics"] })
    },
    onError: () => {
      toast.error("Failed to decline invitation")
    },
  })

  return {
    rejectOrganizerInvite: mutation.mutate,
    rejectOrganizerInviteAsync: mutation.mutateAsync,
    isRejecting: mutation.isPending,
  }
}

export function useResendOrganizerInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ organizerId, inviteNote }: { organizerId: string; inviteNote?: string }) =>
      resendOrganizerInviteApi(ritualId, organizerId, inviteNote),
    onSuccess: () => {
      toast.success("Invitation resent!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["ritual-organizers", ritualId] })
    },
    onError: () => {
      toast.error("Failed to resend invitation")
    },
  })

  return {
    resendOrganizerInvite: mutation.mutate,
    resendOrganizerInviteAsync: mutation.mutateAsync,
    isResending: mutation.isPending,
  }
}

export function useWithdrawOrganizerInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => withdrawOrganizerInviteApi(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation withdrawn!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["ritual-organizers", ritualId] })
    },
    onError: () => {
      toast.error("Failed to withdraw invitation")
    },
  })

  return {
    withdrawOrganizerInvite: mutation.mutate,
    withdrawOrganizerInviteAsync: mutation.mutateAsync,
    isWithdrawing: mutation.isPending,
  }
}

export function useRitualMemberInvitees(ritualId: string) {
  return useQuery({
    queryKey: ["ritual", ritualId, "memberInvitees"],
    queryFn: () => getRitualMemberInvitees(ritualId),
  })
}

export function useInviteRitualMember(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (userId: string) => inviteRitualMember(ritualId, userId),
    onSuccess: () => {
      toast.success("Invitation sent!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId, "memberInvitees"] })
    },
    onError: () => {
      toast.error("Failed to send invitation")
    },
  })

  return {
    inviteRitualMember: mutation.mutate,
    inviteRitualMemberAsync: mutation.mutateAsync,
    isInviting: mutation.isPending,
  }
}

export function useAcceptRitualMemberInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => acceptRitualMemberInvite(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation accepted!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId, "memberInvitees"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
    onError: () => {
      toast.error("Failed to accept invitation")
    },
  })

  return {
    acceptRitualMemberInvite: mutation.mutate,
    acceptRitualMemberInviteAsync: mutation.mutateAsync,
    isAccepting: mutation.isPending,
  }
}

export function useRejectRitualMemberInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => rejectRitualMemberInvite(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation declined")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId, "memberInvitees"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
    onError: () => {
      toast.error("Failed to decline invitation")
    },
  })

  return {
    rejectRitualMemberInvite: mutation.mutate,
    rejectRitualMemberInviteAsync: mutation.mutateAsync,
    isRejecting: mutation.isPending,
  }
}

export function useResendRitualMemberInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => resendRitualMemberInvite(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation resent!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId, "memberInvitees"] })
    },
    onError: () => {
      toast.error("Failed to resend invitation")
    },
  })

  return {
    resendRitualMemberInvite: mutation.mutate,
    resendRitualMemberInviteAsync: mutation.mutateAsync,
    isResending: mutation.isPending,
  }
}

export function useWithdrawRitualMemberInvite(ritualId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (inviteId: string) => withdrawRitualMemberInvite(ritualId, inviteId),
    onSuccess: () => {
      toast.success("Invitation withdrawn!")
      queryClient.invalidateQueries({ queryKey: ["ritual", ritualId, "memberInvitees"] })
    },
    onError: () => {
      toast.error("Failed to withdraw invitation")
    },
  })

  return {
    withdrawRitualMemberInvite: mutation.mutate,
    withdrawRitualMemberInviteAsync: mutation.mutateAsync,
    isWithdrawing: mutation.isPending,
  }
}
